/**
 * Event Marketing Service
 * Handles event calendar, campaign templates, seasonal launches, and performance tracking
 */
import api from './api';

const eventMarketingService = {
  // ==================== Status ====================
  /**
   * Get event marketing status
   */
  getStatus: async () => {
    const response = await api.get('/marketing/events/status');
    return response.data;
  },

  // ==================== Events ====================
  /**
   * Get all marketing events
   */
  getEvents: async (params = {}) => {
    const response = await api.get('/marketing/events', { params });
    return response.data;
  },

  /**
   * Get event by ID
   */
  getEvent: async (eventId) => {
    const response = await api.get(`/marketing/events/${eventId}`);
    return response.data;
  },

  /**
   * Create marketing event
   */
  createEvent: async (eventData) => {
    const response = await api.post('/marketing/events', eventData);
    return response.data;
  },

  /**
   * Update marketing event
   */
  updateEvent: async (eventId, eventData) => {
    const response = await api.put(`/marketing/events/${eventId}`, eventData);
    return response.data;
  },

  /**
   * Delete marketing event
   */
  deleteEvent: async (eventId) => {
    const response = await api.delete(`/marketing/events/${eventId}`);
    return response.data;
  },

  // ==================== Annual Calendar ====================
  /**
   * Create annual event calendar
   */
  createAnnualCalendar: async (calendarConfig) => {
    const response = await api.post('/marketing/events/annual-calendar', calendarConfig);
    return response.data;
  },

  /**
   * Get annual calendar
   */
  getAnnualCalendar: async (year) => {
    const response = await api.get('/marketing/events/annual-calendar', {
      params: { year }
    });
    return response.data;
  },

  // ==================== Campaign Templates ====================
  /**
   * Design campaign templates
   */
  designCampaignTemplates: async (templateConfig) => {
    const response = await api.post('/marketing/events/campaign-templates', templateConfig);
    return response.data;
  },

  /**
   * Get campaign templates
   */
  getCampaignTemplates: async (params = {}) => {
    const response = await api.get('/marketing/events/campaign-templates', { params });
    return response.data;
  },

  /**
   * Get template by ID
   */
  getTemplate: async (templateId) => {
    const response = await api.get(`/marketing/events/campaign-templates/${templateId}`);
    return response.data;
  },

  // ==================== Seasonal Campaigns ====================
  /**
   * Launch Black Friday campaign
   */
  launchBlackFriday: async (campaignConfig) => {
    const response = await api.post('/marketing/events/launch-black-friday', campaignConfig);
    return response.data;
  },

  /**
   * Launch Christmas campaign
   */
  launchChristmas: async (campaignConfig) => {
    const response = await api.post('/marketing/events/launch-christmas', campaignConfig);
    return response.data;
  },

  /**
   * Launch custom seasonal campaign
   */
  launchSeasonalCampaign: async (season, campaignConfig) => {
    const response = await api.post(`/marketing/events/launch-${season}`, campaignConfig);
    return response.data;
  },

  // ==================== Performance Tracking ====================
  /**
   * Track event performance
   */
  trackPerformance: async (trackingConfig) => {
    const response = await api.post('/marketing/events/track-performance', trackingConfig);
    return response.data;
  },

  /**
   * Get event performance metrics
   */
  getPerformanceMetrics: async (eventId, params = {}) => {
    const response = await api.get(`/marketing/events/${eventId}/performance`, { params });
    return response.data;
  },

  /**
   * Compare event performance
   */
  compareEvents: async (eventIds, metrics = []) => {
    const response = await api.post('/marketing/events/compare', {
      event_ids: eventIds,
      metrics
    });
    return response.data;
  },

  // ==================== Discounts ====================
  /**
   * Optimize discount levels
   */
  optimizeDiscounts: async (optimizationConfig) => {
    const response = await api.post('/marketing/discounts/optimization', optimizationConfig);
    return response.data;
  },

  /**
   * Create coupon system
   */
  createCouponSystem: async (couponConfig) => {
    const response = await api.post('/marketing/discounts/coupon-system', couponConfig);
    return response.data;
  },

  /**
   * Design bonus programs
   */
  designBonusPrograms: async (bonusConfig) => {
    const response = await api.post('/marketing/discounts/bonus-programs', bonusConfig);
    return response.data;
  },

  /**
   * Create VIP club system
   */
  createVipClub: async (vipConfig) => {
    const response = await api.post('/marketing/discounts/vip-club', vipConfig);
    return response.data;
  },

  /**
   * Build event marketing calendar
   */
  buildEventCalendar: async (calendarConfig) => {
    const response = await api.post('/marketing/discounts/event-calendar', calendarConfig);
    return response.data;
  },

  /**
   * Get discount agent status
   */
  getDiscountStatus: async () => {
    const response = await api.get('/marketing/discounts/status');
    return response.data;
  }
};

export default eventMarketingService;
